import React from 'react';
import { Facebook, Twitter, Youtube, Instagram, ChevronUp, ArrowUpRight } from 'lucide-react';
import { useTranslate } from '../context/LanguageContext';
import { cn } from '../lib/utils';
import { motion } from 'motion/react';
import { useData } from '../context/DataContext';
import { TikTok } from './TikTokIcon';

export default function Footer() {
  const { language, t } = useTranslate();
  const { settings } = useData();
  const isAr = language === 'ar';
  const socialLinks = settings?.social_links || {};

  // Dynamic Contact Data
  const [dynamicContact, setDynamicContact] = React.useState<any>(null);

  React.useEffect(() => {
    import('../api').then(({ fetchPages }) => {
      fetchPages().then((pages: any) => {
        if (Array.isArray(pages)) {
          const cp = pages.find(p => p.slug === 'contact');
          if (cp && cp.content) {
            try { setDynamicContact(JSON.parse(cp.content)); } catch {}
          }
        }
      }).catch(() => {});
    });
  }, []);

  const emailVal = dynamicContact?.email || "info@nouraldinkouky.com";

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const aboutUsAr = "المنصة الرسمية والبيت الفكري السوداني المستقل للكاتب والسياسي نورالدين كوكى، مخصصة لنشر التحليلات السياسية والدراسات الاستراتيجية العميقة التي تسعى للمساهمة في بناء سودان مستقر، تسوده قيم المواطنة المتساوية، العدالة الاجتماعية، والحرية السلمية المستقلة.";
  const aboutUsEn = "The official platform and independent Sudanese intellectual hub of writer and political activist Noureddine Koki, dedicated to publishing deep political analysis and strategic studies aiming to build a stable Sudan founded on equal citizenship, social justice, and independent peaceful freedoms.";

  const quickLinks = [
    { label: isAr ? "الرئيسية" : "Home", path: "/" },
    { label: isAr ? "من نحن" : "About Us", path: "/about" },
    { label: isAr ? "اتصل بنا" : "Contact Us", path: "/contact" }
  ];

  const sitePolicies = [
    { label: isAr ? "سياسة الخصوصية" : "Privacy Policy", path: "/privacy" },
    { label: isAr ? "اتفاقية الاستخدام" : "Terms of Use", path: "/terms" },
    { label: isAr ? "السياسة التحريرية" : "Editorial Policy", path: "/editorial" }
  ];

  return (
    <footer 
      id="obs-footer"
      dir={isAr ? "rtl" : "ltr"}
      className={cn(
        "relative bg-zinc-950 text-white pt-24 pb-12 transition-all duration-500 overflow-hidden border-t border-zinc-900",
        isAr ? "text-right" : "text-left"
      )}
    >
      {/* 1. CINEMATIC GRADIENT BORDER SCALPEL TOP EDGE (Laser scan effect) */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-secondary/70 to-transparent animate-[pulse_3s_infinite]" />
      <div className="absolute top-[1px] left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-secondary/15 to-transparent blur-[2px]" />

      {/* 2. GLOWING AMBIENT SPACE ATMOSPHERE BACKGROUND */}
      <div className="absolute inset-0 z-0 overflow-hidden select-none pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[35rem] h-[35rem] bg-secondary/[0.04] rounded-full blur-[150px] animate-[pulse_8s_infinite_ease-in-out]" />
        <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-zinc-800/[0.06] rounded-full blur-[130px] animate-[pulse_10s_infinite_ease-in-out_delay-1000]" />
        
        {/* Futuristic Subtle Star Point Mesh */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(217,163,84,0.02)_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-75" />
      </div>

      <div className="container mx-auto px-6 lg:px-16 relative z-10 w-full max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8 mb-20">
          
          {/* About & Branding Section - Spans 4 cols */}
          <div className={cn(
            "lg:col-span-4 flex flex-col space-y-6",
            isAr ? "items-start text-right" : "items-start text-left"
          )}>
            <motion.a 
              href="/" 
              className="group inline-flex items-center gap-3 hover:opacity-95 transition-opacity" 
              aria-label="الشعار الرسمي"
              whileHover={{ scale: 1.02 }}
            >
              <div className="relative w-16 h-16 bg-zinc-900 border border-white/10 flex items-center justify-center shadow-2xl transition-all duration-300 group-hover:border-secondary/40 shrink-0">
                <span className="text-white font-black text-3xl italic tracking-tighter relative z-10 font-serif">NK</span>
                {/* Visual glass sheen overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-secondary/5 via-transparent to-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {/* Premium golden corner micro dot indicator */}
                <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-secondary group-hover:rotate-45 transition-transform duration-300 shadow-md shadow-secondary/40" />
              </div>
            </motion.a>
            <p className="text-xs sm:text-sm text-zinc-400 leading-relaxed max-w-sm font-serif antialiased">
              {isAr ? aboutUsAr : aboutUsEn}
            </p>
          </div>

          {/* Spacer or divider for layout stability */}
          <div className="hidden lg:block lg:col-span-1" />

          {/* Quick Links Section - Spans 2 cols */}
          <div className="lg:col-span-2">
            <h4 className={cn(
              "text-xs sm:text-sm font-black uppercase tracking-wider mb-6 text-zinc-200 transition-colors duration-300",
              isAr ? "border-r-2 pr-3 border-secondary" : "border-l-2 pl-3 border-secondary"
            )}>
              {isAr ? "روابط سريعة" : "Quick Links"}
            </h4>
            <ul className="flex flex-col gap-3.5 text-[13px] text-zinc-400">
              {quickLinks.map((link, idx) => (
                <li key={idx}>
                  <a 
                    href={link.path} 
                    className="hover:text-secondary hover:translate-x-1 inline-flex items-center gap-1.5 transition-all group duration-300"
                  >
                    <span className="w-1 h-1 rounded-full bg-secondary/0 group-hover:bg-secondary/100 transition-all duration-300 shrink-0" />
                    <span>{link.label}</span>
                    <ArrowUpRight size={10} className="opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all text-secondary" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Policy Links Section - Spans 2 cols */}
          <div className="lg:col-span-2">
            <h4 className={cn(
              "text-xs sm:text-sm font-black uppercase tracking-wider mb-6 text-zinc-200 transition-colors duration-300",
              isAr ? "border-r-2 pr-2 border-secondary" : "border-l-2 pl-2 border-secondary"
            )}>
              {isAr ? "سياسات الموقع" : "Observatory Rules"}
            </h4>
            <ul className="flex flex-col gap-3.5 text-[13px] text-zinc-400">
              {sitePolicies.map((link, idx) => (
                <li key={idx}>
                  <a 
                    href={link.path} 
                    className="hover:text-secondary hover:translate-x-1 inline-flex items-center gap-1.5 transition-all group duration-300"
                  >
                    <span className="w-1 h-1 rounded-full bg-secondary/0 group-hover:bg-secondary/100 transition-all duration-300 shrink-0" />
                    <span>{link.label}</span>
                    <ArrowUpRight size={10} className="opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all text-secondary" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social & Contact Section - Spans 3 cols */}
          <div className={cn(
            "lg:col-span-3 flex flex-col space-y-6",
            isAr ? "items-start text-right" : "items-start text-left"
          )}>
            <h4 className={cn(
              "text-xs sm:text-sm font-black uppercase tracking-wider text-zinc-200 transition-colors duration-300",
              isAr ? "border-r-2 pr-3 border-secondary" : "border-l-2 pl-3 border-secondary"
            )}>
              {isAr ? "تابعنا على" : "Follow Us"}
            </h4>
            <div className="flex gap-3">
              {[
                { Icon: Facebook, href: socialLinks.facebook || "#", name: "Facebook" },
                { Icon: Twitter, href: socialLinks.twitter || "#", name: "Twitter" },
                { Icon: Youtube, href: socialLinks.youtube || "#", name: "YouTube" },
                { Icon: Instagram, href: socialLinks.instagram || "#", name: "Instagram" },
                { Icon: TikTok, href: socialLinks.tiktok || "#", name: "TikTok" }
              ].map((item, i) => (
                <motion.a 
                  key={i} 
                  href={item.href} 
                  target={item.href !== "#" ? "_blank" : undefined}
                  rel={item.href !== "#" ? "noopener noreferrer" : undefined}
                  aria-label={item.name}
                  className="w-10 h-10 border border-white/5 bg-zinc-900/50 rounded-xl flex items-center justify-center text-zinc-400 hover:text-white hover:border-secondary hover:bg-secondary/10 hover:shadow-lg hover:shadow-secondary/10 transition-all duration-300"
                  whileHover={{ y: -4, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <item.Icon size={16} />
                </motion.a>
              ))}
            </div>
            <div className="space-y-1 font-mono pt-2">
              <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider block">{isAr ? "البريد الإلكتروني" : "DIRECT EMAIL"}</span>
              <a href={`mailto:${emailVal}`} className="text-xs sm:text-sm text-zinc-300 hover:text-secondary transition-colors font-medium">
                {emailVal}
              </a>
            </div>
          </div>

        </div>

        {/* Elegant Bottom Section & Interactive Scroll to Top */}
        <div className="relative border-t border-white/[0.05] pt-12 flex flex-col sm:flex-row items-center justify-between gap-6">
            <motion.button 
              onClick={scrollToTop}
              className="absolute -top-6 left-1/2 -translate-x-1/2 bg-zinc-900 hover:bg-secondary border border-white/10 hover:border-secondary w-12 h-12 rounded-full flex items-center justify-center text-zinc-400 hover:text-white hover:shadow-2xl hover:shadow-secondary/20 transition-all cursor-pointer group"
              title={isAr ? "الرجوع للأعلى" : "Back to Top"}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <ChevronUp size={20} className="group-hover:animate-bounce" />
            </motion.button>
            
            <p className="text-[11px] sm:text-xs text-zinc-500 font-bold tracking-widest uppercase">
              {isAr ? "جميع الحقوق محفوظة للمرصد الفكري لنورالدين كوكى © 2026" : "All Rights Reserved. Noureddine Koki Intellectual Observatory © 2026"}
            </p>
            
            <div className="flex items-center gap-4 text-[10px] text-zinc-500 font-mono tracking-widest uppercase">
              <span>{isAr ? "المرصد الفكري" : "OBSERVATORY"}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
              <span>{isAr ? "الكلمة أمانة" : "TRUTH FIRST"}</span>
            </div>
        </div>
      </div>
    </footer>
  );
}
